import type { ReactNode } from "react";
import { colors, fontSize, fontWeight, radius, space } from "../../lib/tokens";

interface Props {
  /** Whether there is anything to save. Drives both buttons' enabled state. */
  dirty: boolean;
  saving: boolean;
  error?: string | null;
  /** Blocks Save while leaving Cancel usable — e.g. copy over the length cap. */
  saveBlocked?: boolean;
  /** Shown at the left of the row: unsaved count, validation warning, etc. */
  status?: ReactNode;
  onCancel: () => void;
  onSave: () => void;
}

/**
 * The save bar every tenant editor ends with: an error banner, then Cancelar +
 * Salvar wired to a dirty flag.
 *
 * Extracted when `TenantFeatures` became the third hand-rolled copy of the same
 * markup (after `TenantSettingsEditor` and `VocabularyEditor`) — three copies of
 * identical inline styles is where "it is only a footer" stops being true.
 */
export function EditorFooter({
  dirty,
  saving,
  error,
  saveBlocked = false,
  status,
  onCancel,
  onSave,
}: Props) {
  const cancelDisabled = saving || !dirty;
  const saveDisabled = cancelDisabled || saveBlocked;

  return (
    <>
      {error && (
        <div style={{
          marginBottom: space.md, padding: space.md, background: colors.errorSoft,
          color: colors.error, borderRadius: radius.sm, fontSize: fontSize.md,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: space.sm, justifyContent: "flex-end", alignItems: "center" }}>
        {status !== undefined && (
          <span style={{ marginRight: "auto", fontSize: fontSize.sm, color: colors.warning }}>
            {status}
          </span>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelDisabled}
          style={{
            height: 36, padding: `0 ${space.lg}px`, background: colors.white, color: colors.ink,
            border: `1px solid ${colors.fog}`, borderRadius: radius.sm,
            fontSize: fontSize.md, fontWeight: fontWeight.medium,
            cursor: cancelDisabled ? "not-allowed" : "pointer",
            opacity: cancelDisabled ? 0.5 : 1,
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saveDisabled}
          style={{
            height: 36, padding: `0 ${space.lg}px`, background: colors.ink, color: colors.paper,
            border: "none", borderRadius: radius.sm,
            fontSize: fontSize.md, fontWeight: fontWeight.medium,
            cursor: saveDisabled ? "not-allowed" : "pointer",
            opacity: saveDisabled ? 0.5 : 1,
          }}
        >
          {saving ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </>
  );
}
